import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { ShieldCheck, ShieldX, Building2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { verifyVendorContract } from "@/services/public-vendor-contract"

function formatDate(iso: string | null): string {
  if (!iso) return ""
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })
}

// Vérification publique du certificat d'un contrat signé (§4.3).
// Réponse zéro-PII : confirme uniquement l'intégrité (hash) du PDF que le
// porteur détient déjà. Aucun nom, email ou IP n'est exposé ici.
export default function VerifyVendorContractPage() {
  const { token } = useParams<{ token: string }>()

  const query = useQuery({
    queryKey: ["verify-vendor-contract", token],
    queryFn: () => verifyVendorContract(token!),
    enabled: Boolean(token),
    retry: false,
  })

  const valid = query.data?.valid === true

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4 sm:px-6">
          <span className="flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-slate-500">
            <Building2 className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Vérification de signature</p>
            <p className="text-sm font-medium text-slate-900">Qualneo</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        {query.isLoading ? (
          <p className="text-center text-muted-foreground" data-test="verify-loading">
            Vérification en cours...
          </p>
        ) : query.isError || !valid ? (
          <Card className="border-rose-200 bg-rose-50" data-test="verify-invalid">
            <CardContent className="space-y-2 py-12 text-center text-rose-900">
              <ShieldX className="mx-auto h-10 w-10" />
              <h1 className="text-lg font-semibold">Certificat introuvable</h1>
              <p className="text-sm">
                Ce certificat de signature est inconnu. Vérifiez le lien complet figurant sur le document.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-200 bg-emerald-50" data-test="verify-valid">
            <CardContent className="space-y-4 py-10 text-emerald-900">
              <div className="text-center">
                <ShieldCheck className="mx-auto h-10 w-10" />
                <h1 className="mt-2 text-lg font-semibold">Signature authentique</h1>
                <p className="text-sm">Ce contrat a bien été signé via Qualneo et n'a pas été altéré.</p>
              </div>
              <dl className="grid gap-3 rounded-lg border border-emerald-200 bg-white/60 p-4 text-sm sm:grid-cols-2">
                {query.data?.contract_ref ? (
                  <div>
                    <dt className="text-xs text-muted-foreground">Référence du contrat</dt>
                    <dd className="font-medium" data-test="verify-contract-ref">
                      {query.data.contract_ref}
                    </dd>
                  </div>
                ) : null}
                {query.data?.signed_at ? (
                  <div>
                    <dt className="text-xs text-muted-foreground">Signé le</dt>
                    <dd data-test="verify-signed-at">{formatDate(query.data.signed_at)}</dd>
                  </div>
                ) : null}
                {query.data?.pdf_sha256 ? (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-muted-foreground">Empreinte du document (SHA-256)</dt>
                    <dd className="break-all font-mono text-xs" data-test="verify-hash">
                      {query.data.pdf_sha256}
                    </dd>
                  </div>
                ) : null}
              </dl>
              <p className="text-center text-xs text-emerald-800">
                Comparez cette empreinte avec celle imprimée sur votre exemplaire pour confirmer l'intégrité.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  )
}
